import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Box,
  TextField,
  InputAdornment,
  Paper,
  ClickAwayListener,
  Chip,
  Tooltip,
  Skeleton,
  Typography,
  IconButton,
  Popper,
  Fade,
  useTheme,
} from "@mui/material";
import {
  useProjects,
  validateSelectedProjects,
} from "../contexts/ProjectContext";
import { useThemeMode } from "../contexts/ThemeContext";
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Clear as ClearIcon,
  MoreHoriz as MoreHorizIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import CreateProjectForm from "./CreateProjectForm";
import { useStoredTheme } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";
const MAX_CHIPS_DISPLAY = 2; // Maximum number of chips to display before showing "more"

// Custom event to notify the application about project selection changes
export const PROJECT_SELECTION_CHANGED_EVENT = "project-selection-changed";

const ProjectSwitcher = () => {
  const { t } = useTranslation();
  const storedTheme = useStoredTheme();

  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedProjects,
    availableProjects,
    toggleProject,
    removeProject,
    getProjectName,
    loading,
    error,
    refreshProjects,
  } = useProjects();

  // Use utility function to ensure we always have valid arrays with stable references
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects),
    [selectedProjects],
  );
  const safeAvailableProjects = useMemo(
    () => (Array.isArray(availableProjects) ? availableProjects : []),
    [availableProjects],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(280);
  const prevSelectedProjectsRef = useRef(safeSelectedProjects);
  const [showGuideTooltip, setShowGuideTooltip] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { mode } = useThemeMode();
  const theme = useTheme();

  // Update input width when dropdown opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      // Make input width match button width exactly, no extra padding
      const buttonWidth = buttonRef.current.offsetWidth;
      setInputWidth(buttonWidth);
    }
  }, [isOpen]);

  // Monitor changes to selectedProjects and dispatch custom event when they change
  useEffect(() => {
    const prevProjects = prevSelectedProjectsRef.current;

    // Check if the selected projects have actually changed
    const hasChanged =
      prevProjects.length !== safeSelectedProjects.length ||
      prevProjects.some(
        (project, index) => project !== safeSelectedProjects[index],
      );

    if (hasChanged) {
      prevSelectedProjectsRef.current = safeSelectedProjects;

      // Dispatch a custom event that can be listened for in other components
      window.dispatchEvent(
        new CustomEvent(PROJECT_SELECTION_CHANGED_EVENT, {
          detail: {
            selectedProjects: safeSelectedProjects,
            path: location.pathname,
          },
        }),
      );
    }
  }, [safeSelectedProjects, location.pathname]);

  // Show tooltip if no projects are available after loading completes
  useEffect(() => {
    if (!loading && safeAvailableProjects.length === 0 && !createFormOpen) {
      // Only show tooltip after a short delay to avoid it showing immediately on first load
      const timer = setTimeout(() => {
        setShowGuideTooltip(true);
        setTooltipOpen(true);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShowGuideTooltip(false);
      setTooltipOpen(false);
    }
  }, [loading, safeAvailableProjects.length, createFormOpen]);

  // Auto-hide tooltip after 8 seconds
  useEffect(() => {
    if (tooltipOpen) {
      const timer = setTimeout(() => {
        setTooltipOpen(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [tooltipOpen]);

  const handleClick = (event) => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleProjectToggle = useCallback(
    (projectId) => {
      toggleProject(projectId);
    },
    [toggleProject],
  );

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClickAway = (event) => {
    // Don't close if clicking on the search input
    if (
      searchInputRef.current &&
      searchInputRef.current.contains(event.target)
    ) {
      return;
    }
    handleClose();
  };

  const handleRemoveProject = (event, projectId) => {
    event.stopPropagation(); // Prevent the dropdown from opening
    removeProject(projectId);
  };

  const handleCreateProjectClick = () => {
    setCreateFormOpen(true);
    setIsOpen(false); // Close the dropdown
  };

  const handleCreateFormClose = () => {
    setCreateFormOpen(false);
  };

  const handleManageProjectsClick = () => {
    setIsOpen(false); // Close the dropdown
    navigate("/projects"); // Navigate to projects management page
  };

  const handleRefreshProjects = (e) => {
    e.stopPropagation();
    refreshProjects();
  };

  const filteredProjects = safeAvailableProjects.filter(
    (project) =>
      project?.name &&
      typeof project.name === "string" &&
      project.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Get button content based on selection
  const getButtonContent = () => {
    // Show loading skeleton when projects are loading
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <FolderIcon sx={{ color: "action.disabled" }} />
          <Skeleton variant="text" width={120} sx={{ marginInlineStart: 1 }} />
        </Box>
      );
    }

    // Show error state if there was an error loading projects
    if (error) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            color: "error.main",
          }}
        >
          <FolderIcon sx={{ color: "error.main" }} />
          <Box
            sx={{ marginInlineStart: 1, display: "flex", alignItems: "center" }}
          >
            <Typography variant="body2" sx={{ marginInlineEnd: 1 }}>
              {t('projects.errorLoadingProjects')}
            </Typography>
            <IconButton
              size="small"
              onClick={handleRefreshProjects}
              sx={{ marginInlineStart: "auto" }}
            >
              <RefreshIcon className="rtl:scale-x-[-1]" fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      );
    }

    if (safeSelectedProjects.includes("all")) {
      return (
        <>
          <FolderIcon />
          <Box sx={{ marginInlineStart: 1 }}>{t('projects.allProjects')}</Box>
        </>
      );
    } else if (safeSelectedProjects.length === 1) {
      // Single project selected
      const projectName = getProjectName(safeSelectedProjects[0]);
      // Only show the project if we have its data, otherwise show a fallback
      return (
        <>
          <FolderIcon />
          <Box
            sx={{
              marginInlineStart: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {projectName !== "Unknown Project"
              ? projectName
              : t('projects.loadingProject')}
          </Box>
        </>
      );
    } else {
      // Multiple projects selected - show chips with limit
      const displayProjects = safeSelectedProjects.slice(0, MAX_CHIPS_DISPLAY);
      const extraCount = safeSelectedProjects.length - MAX_CHIPS_DISPLAY;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <FolderIcon sx={{ marginInlineEnd: 1, flexShrink: 0 }} />
          <Box sx={{ display: "flex", flexWrap: "nowrap", overflow: "hidden" }}>
            {displayProjects.map((projectId) => {
              const projectName = getProjectName(projectId);
              // Skip rendering chips for unknown projects during loading
              if (projectName === "Unknown Project" && loading) {
                return (
                  <Skeleton
                    key={projectId}
                    variant="rounded"
                    width={80}
                    height={24}
                    sx={{ marginInlineEnd: 0.5 }}
                  />
                );
              }

              return (
                <Chip
                  key={projectId}
                  size="small"
                  label={
                    projectName !== "Unknown Project"
                      ? projectName
                      : t('common.loading')
                  }
                  onDelete={(e) => handleRemoveProject(e, projectId)}
                  deleteIcon={<ClearIcon fontSize="small" />}
                  sx={{
                    marginInlineEnd: 0.5,
                    height: 24,
                    maxWidth: 120,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              );
            })}
            {extraCount > 0 && (
              <Tooltip
                title={
                  loading
                    ? t('projects.loadingProjects')
                    : safeSelectedProjects
                        .slice(MAX_CHIPS_DISPLAY)
                        .map((id) => getProjectName(id))
                        .join(", ")
                }
              >
                <Chip
                  size="small"
                  label={t('projects.moreCount', { count: extraCount })}
                  icon={<MoreHorizIcon fontSize="small" />}
                  sx={{ height: 24 }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      );
    }
  };

  const currentOrganizationName = localStorage.getItem(
    "currentOrganizationName",
  );

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: "relative" }}>
          {showGuideTooltip && (
            <Popper
              open={tooltipOpen}
              anchorEl={buttonRef.current}
              placement="bottom-start"
              transition
              disablePortal
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
              ]}
              sx={{ zIndex: 1200 }}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      maxWidth: 280,
                      borderRadius: 1,
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -10,
                        insetInlineStart: 20,
                        borderWidth: "0 10px 10px",
                        borderStyle: "solid",
                        borderColor: "transparent transparent",
                        borderBottomColor: storedTheme.card.borderColor[mode],
                        zIndex: 0,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: -9,
                        insetInlineStart: 20,
                        borderWidth: "0 10px 10px",
                        borderStyle: "solid",
                        borderColor: "transparent transparent",
                        borderBottomColor:
                          storedTheme.card.backgroundColor[mode],
                        zIndex: 1,
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      {t('projects.createFirstProject')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1.5,
                        fontSize: "0.85rem",
                        opacity: mode === "light" ? 0.95 : 0.8,
                      }}
                    >
                      {t('projects.projectHelpText')}
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={handleCreateProjectClick}
                      startIcon={<AddIcon fontSize="small" />}
                      sx={{
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        py: 0.5,
                        px: 1.5,
                        minWidth: "auto",
                        height: 28,
                        fontWeight: 500,
                        "& .MuiButton-startIcon": {
                          marginInlineEnd: 0.5,
                        },
                      }}
                    >
                      {t('projects.createProject')}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => setTooltipOpen(false)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        insetInlineEnd: 8,
                        opacity: 0.7,
                        padding: 0.5,
                        fontSize: "0.75rem",
                        "&:hover": {
                          opacity: 1,
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Paper>
                </Fade>
              )}
            </Popper>
          )}

          <Box ref={buttonRef}>
            {isOpen ? (
              <TextField
                inputRef={searchInputRef}
                autoFocus
                variant="outlined"
                placeholder={t('projects.searchProjects')}
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FolderIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <KeyboardArrowUpIcon
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            color: "primary.main",
                          },
                        }}
                        onClick={() => handleClose()}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: inputWidth,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0.5,
                    height: 40, // More compact height
                    padding: "0 8px", // Reduce horizontal padding
                    "& fieldset": {
                      borderColor: "divider",
                      borderWidth: "1px",
                    },
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 0", // Reduce vertical padding
                    fontSize: "0.875rem", // Slightly smaller font
                  },
                  "& .MuiInputAdornment-root": {
                    marginInlineEnd: 0, // Reduce spacing for adornments
                    "& .MuiSvgIcon-root": {
                      fontSize: "1.2rem", // Smaller icon size
                    },
                  },
                }}
              />
            ) : (
              <Button
                variant="outlined"
                onClick={handleClick}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  color: "text.primary",
                  borderColor: "divider",
                  borderRadius: 0.5,
                  px: 2,
                  py: 1,
                  display: "flex",
                  justifyContent: "flex-start",
                  textAlign: "start",
                  maxWidth: 320,
                  minWidth: 180, // Add minimum width to prevent button jumping during loading
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                    color: "primary.main",
                  },
                  "& .MuiButton-endIcon": {
                    marginInlineStart: "auto",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.2rem",
                  },
                }}
                disabled={loading} // Disable button during loading
              >
                {getButtonContent()}
              </Button>
            )}
          </Box>

          {isOpen && (
            <Paper
              elevation={1}
              sx={{
                position: "absolute",
                top: "100%",
                insetInlineStart: 0,
                insetInlineEnd: 0,
                width: inputWidth,
                zIndex: 1300,
                mt: 0.5,
                borderRadius: 0.5,
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* Organization Context Section */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(0, 0, 0, 0.01)",
                }}
              >
                {currentOrganizationName ? (
                  <Tooltip
                    title={t('organizations.switchOrganizationTooltip')}
                    placement="bottom"
                    arrow
                  >
                    <Box
                      onClick={() => {
                        navigate("/settings?tab=organizations");
                        setIsOpen(false);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        p: 1,
                        borderRadius: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(19, 211, 158, 0.08)"
                              : "rgba(19, 211, 158, 0.04)",
                        },
                      }}
                    >
                      <BusinessIcon
                        sx={{
                          color: "primary.main",
                          fontSize: "1.1rem",
                          marginInlineEnd: 1.5,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            lineHeight: 1,
                          }}
                        >
                          {t('organizations.organizationProjects')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "primary.main",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {currentOrganizationName}
                        </Typography>
                      </Box>
                      <SwapHorizIcon
                        sx={{
                          color: "text.secondary",
                          fontSize: "1rem",
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={t('organizations.joinOrganizationTooltip')}
                    placement="bottom"
                    arrow
                  >
                    <Box
                      onClick={() => {
                        navigate("/settings?tab=organizations");
                        setIsOpen(false);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        p: 1,
                        borderRadius: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(19, 211, 158, 0.08)"
                              : "rgba(19, 211, 158, 0.04)",
                        },
                      }}
                    >
                      <PersonIcon
                        sx={{
                          color: "text.secondary",
                          fontSize: "1.1rem",
                          marginInlineEnd: 1.5,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            lineHeight: 1,
                          }}
                        >
                          {t('projects.personalProjects')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                            fontStyle: "italic",
                          }}
                        >
                          {t('organizations.clickToJoin')}
                        </Typography>
                      </Box>
                      <AddIcon
                        sx={{
                          color: "primary.main",
                          fontSize: "1rem",
                          opacity: 0.8,
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <MenuItem
                onClick={() => handleProjectToggle("all")}
                sx={{
                  py: 1,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    edge="start"
                    checked={safeSelectedProjects.includes("all")}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                      padding: 0.5,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t('projects.allProjects')}
                  primaryTypographyProps={{
                    fontWeight: safeSelectedProjects.includes("all")
                      ? 600
                      : 400,
                    fontSize: "0.9rem",
                  }}
                />
              </MenuItem>
              <Divider sx={{ my: 0.5, opacity: 0.7 }} />

              <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                {loading ? (
                  // Show skeleton loaders during loading instead of just a spinner
                  <Box sx={{ px: 1.5, py: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((item) => (
                      <Box
                        key={item}
                        sx={{ display: "flex", alignItems: "center", my: 1 }}
                      >
                        <Skeleton
                          variant="circular"
                          width={20}
                          height={20}
                          sx={{ marginInlineEnd: 1.5 }}
                        />
                        <Skeleton
                          variant="text"
                          width={Math.floor(Math.random() * 40) + 80}
                          height={20}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : filteredProjects.length > 0 ? (
                  filteredProjects.map((project) => (
                    <MenuItem
                      key={project.id}
                      onClick={() => handleProjectToggle(project.id)}
                      sx={{
                        py: 0.75,
                        px: 1.5,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Checkbox
                          edge="start"
                          checked={safeSelectedProjects.includes(project.id)}
                          tabIndex={-1}
                          disableRipple
                          sx={{
                            "&.Mui-checked": {
                              color: "primary.main",
                            },
                            padding: 0.5,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={project.name}
                        primaryTypographyProps={{
                          fontWeight: safeSelectedProjects.includes(project.id)
                            ? 600
                            : 400,
                          fontSize: "0.9rem",
                        }}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <Box
                    sx={{
                      py: 2,
                      px: 1.5,
                      color: "text.secondary",
                      fontSize: "0.9rem",
                      textAlign: "center",
                    }}
                  >
                    {searchQuery ? (
                      t('projects.noMatchingProjects')
                    ) : (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {t('projects.noProjectsAvailable')}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={handleCreateProjectClick}
                            sx={{
                              mt: 0.5,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              borderColor: "primary.main",
                              color: "primary.main",
                              py: 0.5,
                              px: 1,
                              minWidth: "auto",
                              height: 26,
                              "& .MuiButton-startIcon": {
                                marginInlineEnd: 0.5,
                              },
                            }}
                          >
                            {t('projects.createProject')}
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={
                              <RefreshIcon
                                className="rtl:scale-x-[-1]"
                                fontSize="small"
                              />
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              refreshProjects();
                            }}
                            sx={{
                              mt: 0.5,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              py: 0.5,
                              px: 1,
                              minWidth: "auto",
                              height: 26,
                              "& .MuiButton-startIcon": {
                                marginInlineEnd: 0.5,
                              },
                            }}
                          >
                            {t('common.retry')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 0.5, opacity: 0.7 }} />

              <MenuItem
                onClick={handleCreateProjectClick}
                sx={{
                  py: 1,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <AddIcon fontSize="small" sx={{ color: "primary.main" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('projects.createNewProject')}
                  primaryTypographyProps={{
                    color: "primary.main",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                  }}
                />
              </MenuItem>

              <MenuItem
                onClick={handleManageProjectsClick}
                sx={{
                  py: 1,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <SettingsIcon
                    fontSize="small"
                    sx={{ color: "primary.main" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t('projects.manageProjects')}
                  primaryTypographyProps={{
                    color: "primary.main",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                  }}
                />
              </MenuItem>
            </Paper>
          )}
        </Box>
      </ClickAwayListener>

      {/* Create Project Form Dialog */}
      <CreateProjectForm
        open={createFormOpen}
        onClose={handleCreateFormClose}
      />
    </>
  );
};

export default ProjectSwitcher;
